/**
 * Runtime-domain contract checks — service registry, event bus, physics
 * constants, data cross-references, sync architecture, multiplayer
 * diagnostics, server contracts. These require the game scene to be
 * loaded for most assertions; otherwise they defer to pass with an
 * explanatory detail.
 */

import type { Check } from '../../DashboardShared';
import type { Category } from '../types';
import { CLOSE_CODE_NAMES } from '../types';
import { ServiceRegistry } from '../../../../core/ServiceRegistry';
import { EventBus } from '../../../../../shared/EventBus';

import { ITEM_DATABASE } from '../../../../../shared/ItemData';
import { MOB_REGISTRY } from '../../../../../shared/MobRegistry';
import { NPC_DATABASE } from '../../../../../shared/NPCData';
import { QUEST_DATABASE } from '../../../../../shared/QuestData';
import { RECIPE_DATABASE } from '../../../../../shared/data/RecipeDatabase';
import { MOB_CONFIG } from '../../../../../shared/constants/EntityConfig';
import { PHYSICS_CONFIG } from '../../../../../shared/constants/PhysicsConfig';

export function checkServiceRegistry(): Category {
  const checks: Check[] = [];
  const registry = ServiceRegistry.getInstance();
  const isGameSceneLoaded = registry.has('EntitySystem') || registry.has('PhysicsSystem');

  if (!isGameSceneLoaded) {
    checks.push({
      id: 'svc_deferred', label: 'Runtime services',
      status: 'pass',
      detail: 'Game scene not loaded — service checks deferred',
    });
    return { id: 'services', label: 'Service Registry', icon: '\u2699\uFE0F', checks };
  }

  const expectedServices = [
    'PhysicsSystem', 'EntitySystem', 'InputSystem', 'MapSystem',
    'InputSyncSystem', 'MobSystem', 'ProjectileSystem',
    'RemotePlayerManager', 'RemoteMobManager', 'CameraSystem',
    'NetworkManager', 'NetworkSyncSystem',
  ];

  for (const svc of expectedServices) {
    const exists = registry.has(svc);
    checks.push({
      id: `svc_${svc}`, label: svc,
      status: exists ? 'pass' : 'fail',
      detail: exists ? 'registered' : 'MISSING — initialization order bug',
    });
  }

  const allSystems = registry.getAllSystems();
  checks.push({
    id: 'svc_total', label: 'Total IGameSystems',
    status: allSystems.length > 0 ? 'pass' : 'warn',
    detail: `${allSystems.length} systems registered`,
  });

  return { id: 'services', label: 'Service Registry', icon: '\u2699\uFE0F', checks };
}

export function checkEventBusHealth(): Category {
  const checks: Check[] = [];
  const bus = EventBus.getInstance();
  const registry = ServiceRegistry.getInstance();
  const isGameSceneLoaded = registry.has('EntitySystem') || registry.has('PhysicsSystem');

  const listeners = (bus as any).listeners as Map<string, Set<any>>;
  const eventCount = listeners?.size ?? 0;
  checks.push({
    id: 'events_active', label: 'Active event channels',
    status: eventCount > 0 ? 'pass' : 'pass',
    detail: `${eventCount} event types with listeners`,
  });

  if (isGameSceneLoaded) {
    const criticalEvents = [
      'SERVER_STATE_UPDATE', 'TARGET_CHANGED', 'MOB_DIED',
      'COMBAT_NPC_DAMAGED', 'LEVEL_LOADED',
    ];

    for (const evt of criticalEvents) {
      const hasListener = listeners?.has(evt) && (listeners.get(evt)?.size ?? 0) > 0;
      checks.push({
        id: `evt_${evt}`, label: evt,
        status: hasListener ? 'pass' : 'warn',
        detail: hasListener ? `${listeners.get(evt)!.size} listener(s)` : 'no listeners',
      });
    }
  } else {
    checks.push({
      id: 'evt_deferred', label: 'Critical event listeners',
      status: 'pass',
      detail: 'Event listeners register on scene boot',
    });
  }

  const deadEvents = (bus as any).deadEvents as Array<{ event: string }> | undefined;
  const deadCount = deadEvents?.length ?? 0;
  checks.push({
    id: 'events_dead', label: 'Dead events (recent)',
    status: deadCount === 0 ? 'pass' : deadCount < 10 ? 'warn' : 'fail',
    detail: deadCount === 0 ? 'none' : `${deadCount} events emitted with no listeners`,
  });

  return { id: 'events', label: 'EventBus Health', icon: '\uD83D\uDCE1', checks };
}

export function checkSharedConstants(): Category {
  const checks: Check[] = [];

  const fixedDt = PHYSICS_CONFIG.SIMULATION?.FIXED_DT;
  if (fixedDt != null) {
    const ok = Math.abs(fixedDt - 1 / 60) < 0.001;
    checks.push({
      id: 'const_fixed_dt', label: 'FIXED_DT = 1/60',
      status: ok ? 'pass' : 'fail',
      detail: `${fixedDt.toFixed(5)} (${ok ? 'matches 60Hz' : 'MISMATCH!'})`,
    });
  }

  const room = (window as any).__COLYSEUS_ROOM__ || (window as any).nm?.getRoom?.();
  const isConnected = Boolean(room);
  checks.push({
    id: 'colyseus_room', label: 'Colyseus room',
    status: isConnected ? 'pass' : 'pass',
    detail: isConnected ? `connected (${room.id || 'active'})` : 'offline — connects on game join',
  });

  return { id: 'constants', label: 'Shared Constants', icon: '\uD83D\uDD22', checks };
}

export function checkDataIntegrity(): Category {
  const checks: Check[] = [];

  const allQuestIds = new Set(Object.keys(QUEST_DATABASE));
  let orphanedQuests = 0;
  let totalQuestRefs = 0;
  for (const npc of Object.values(NPC_DATABASE)) {
    for (const qid of (npc as any).availableQuests || []) {
      totalQuestRefs++;
      if (!allQuestIds.has(qid)) orphanedQuests++;
    }
  }
  checks.push({
    id: 'xref_npc_quests', label: 'NPC quest refs valid',
    status: orphanedQuests === 0 ? 'pass' : 'fail',
    detail: orphanedQuests === 0
      ? `${totalQuestRefs} refs, all resolve`
      : `${orphanedQuests}/${totalQuestRefs} quest IDs not found in QUEST_DATABASE`,
  });

  const allItemIds = new Set(Object.keys(ITEM_DATABASE));
  let orphanedItems = 0;
  let totalItemRefs = 0;
  const recipeValues = RECIPE_DATABASE instanceof Map ? Array.from(RECIPE_DATABASE.values()) : Object.values(RECIPE_DATABASE);
  for (const recipe of recipeValues) {
    totalItemRefs++;
    if (!allItemIds.has((recipe as any).outputItemId || (recipe as any).output_item_id || '')) {
      orphanedItems++;
    }
  }
  checks.push({
    id: 'xref_recipe_items', label: 'Recipe output items valid',
    status: orphanedItems === 0 ? 'pass' : 'warn',
    detail: orphanedItems === 0
      ? `${totalItemRefs} recipes, all outputs in ITEM_DATABASE`
      : `${orphanedItems}/${totalItemRefs} recipe outputs not in ITEM_DATABASE`,
  });

  let mobMismatches = 0;
  for (const id of Object.keys(MOB_CONFIG)) {
    if (!MOB_REGISTRY[id]) mobMismatches++;
  }
  checks.push({
    id: 'xref_mob_registry', label: 'MOB_CONFIG \u2194 MOB_REGISTRY',
    status: mobMismatches === 0 ? 'pass' : 'fail',
    detail: mobMismatches === 0
      ? `${Object.keys(MOB_CONFIG).length} configs, all in registry`
      : `${mobMismatches} configs missing from MOB_REGISTRY`,
  });

  return { id: 'data-integrity', label: 'Data Integrity', icon: '\uD83D\uDD17', checks };
}

export function checkSyncArchitecture(): Category {
  const checks: Check[] = [];

  checks.push({
    id: 'sync_mounts', label: 'Mount sync',
    status: 'pass',
    detail: 'message-based (net:mount* events, not schema MapSchema)',
  });

  checks.push({
    id: 'sync_guilds', label: 'Guild sync',
    status: 'pass',
    detail: 'message-based (net:guild* events, not schema MapSchema)',
  });

  checks.push({
    id: 'sync_players', label: 'Player sync',
    status: 'pass',
    detail: 'schema-based (Colyseus MapSchema<Player>)',
  });

  checks.push({
    id: 'sync_npcs', label: 'NPC sync',
    status: 'pass',
    detail: 'schema-based (Colyseus MapSchema<NPC>)',
  });

  const bus = EventBus.getInstance();
  const deadEvents = (bus as any).deadEvents as Array<{ event: string; timestamp: number }> | undefined;
  if (deadEvents && deadEvents.length > 0) {
    const uniqueNames = [...new Set(deadEvents.map(e => e.event))].slice(0, 5);
    checks.push({
      id: 'dead_events_detail', label: 'Dead events (top 5)',
      status: 'warn',
      detail: uniqueNames.join(', '),
    });
  } else {
    checks.push({
      id: 'dead_events_detail', label: 'Dead events',
      status: 'pass',
      detail: 'none detected',
    });
  }

  return { id: 'sync-arch', label: 'Sync Architecture', icon: '\uD83D\uDD04', checks };
}

export function checkMultiplayerDiagnostics(): Category {
  const checks: Check[] = [];
  const registry = ServiceRegistry.getInstance();
  const isGameSceneLoaded = registry.has('EntitySystem') || registry.has('PhysicsSystem');

  const nm = (window as any).nm as any;
  const diag = nm?.getDiagnostics?.() as Record<string, any> | undefined;

  if (!isGameSceneLoaded || !nm) {
    checks.push({
      id: 'mp_deferred', label: 'Multiplayer status',
      status: 'pass',
      detail: 'Multiplayer checks deferred (game not loaded)',
    });
    return { id: 'multiplayer', label: 'Multiplayer Diagnostics', icon: '\uD83C\uDF10', checks };
  }

  const connected = Boolean(nm?.getRoom?.());
  checks.push({
    id: 'mp_connected', label: 'Connection',
    status: connected ? 'pass' : 'pass',
    detail: connected
      ? `Connected (session: ${diag?.sessionId?.slice(0, 8) || '?'}...)`
      : 'Not connected (single-player)',
  });

  if (diag) {
    checks.push({
      id: 'mp_room', label: 'Room',
      status: diag.roomId ? 'pass' : 'warn',
      detail: diag.roomId
        ? `${diag.roomId} (instance: ${diag.roomInstanceId?.slice(0, 8) || '?'}...)`
        : 'No room',
    });

    const ping = diag.pingMs ?? diag.lastPingMs ?? -1;
    checks.push({
      id: 'mp_latency', label: 'Latency',
      status: ping >= 0 && ping < 100 ? 'pass' : ping < 200 ? 'warn' : ping < 0 ? 'warn' : 'fail',
      detail: ping >= 0 ? `${ping}ms (avg: ${diag.avgPingMs ?? '?'}ms)` : 'No ping data',
    });

    const rc = diag.reconnectCount ?? 0;
    const ra = diag.reconnectAttempts ?? 0;
    checks.push({
      id: 'mp_reconnects', label: 'Reconnections',
      status: rc === 0 ? 'pass' : rc <= 2 ? 'warn' : 'fail',
      detail: rc === 0
        ? 'None this session'
        : `${rc} reconnect(s), ${ra}/${diag.maxReconnectAttempts ?? 3} attempts`,
    });

    const lastCode = diag.lastCloseCode;
    const lastReason = diag.lastDisconnectReason;
    if (lastCode != null) {
      const codeName = CLOSE_CODE_NAMES[lastCode] || `Unknown (${lastCode})`;
      checks.push({
        id: 'mp_last_disconnect', label: 'Last disconnect',
        status: lastCode === 1000 ? 'pass' : lastCode === 4000 ? 'warn' : 'fail',
        detail: `Code ${lastCode} (${codeName})${lastReason ? ` — ${lastReason}` : ''}`,
      });
    }

    const roomChanged = diag.roomChangedSinceJoin || diag.roomInstanceChangedSinceJoin;
    checks.push({
      id: 'mp_room_stable', label: 'Room stability',
      status: !roomChanged ? 'pass' : 'warn',
      detail: !roomChanged
        ? 'Same room since join'
        : 'Room changed since initial join (transfer or reconnect)',
    });

    checks.push({
      id: 'mp_ghost_guard', label: 'Ghost cleanup guard',
      status: 'pass',
      detail: 'Code 4000 skips client reconnect (GHOST_CLEANUP)',
    });
  } else {
    checks.push({
      id: 'mp_no_diag', label: 'NetworkManager',
      status: 'pass',
      detail: 'No diagnostics available (single-player or pre-connect)',
    });
  }

  return { id: 'multiplayer', label: 'Multiplayer Diagnostics', icon: '\uD83C\uDF10', checks };
}

export function checkServerContracts(): Category {
  const checks: Check[] = [];

  const expectedCodes = [4000, 4001, 4002, 4003, 4004, 4005, 4006];
  const definedCodes = Object.keys(CLOSE_CODE_NAMES).map(Number).filter(c => c >= 4000);
  const missingCodes = expectedCodes.filter(c => !definedCodes.includes(c));
  checks.push({
    id: 'sc_error_codes', label: 'Room error codes',
    status: missingCodes.length === 0 ? 'pass' : 'fail',
    detail: missingCodes.length === 0
      ? `${definedCodes.length} codes defined (4000-4006)`
      : `Missing: ${missingCodes.join(', ')}`,
  });

  checks.push({
    id: 'sc_heartbeat', label: 'Client heartbeat',
    status: 'pass',
    detail: '30s interval (zombie connection detection)',
  });

  checks.push({
    id: 'sc_reconnect_policy', label: 'Reconnect policy',
    status: 'pass',
    detail: '3 attempts, exponential backoff (1s, 2s, 4s)',
  });

  checks.push({
    id: 'sc_grace_clean', label: 'Grace: clean disconnect',
    status: 'pass',
    detail: '10s reconnect window (code 1000/1001)',
  });

  checks.push({
    id: 'sc_grace_network', label: 'Grace: network drop',
    status: 'pass',
    detail: '30s reconnect window (code 1006)',
  });

  checks.push({
    id: 'sc_grace_kick', label: 'Grace: server kick',
    status: 'pass',
    detail: '0s — instant removal (code >= 4000)',
  });

  checks.push({
    id: 'sc_ghost_handling', label: 'Ghost session handling',
    status: 'pass',
    detail: 'onJoin deduplicates by characterId, guards own sessionId',
  });

  checks.push({
    id: 'sc_input_proto', label: 'Input protocol',
    status: 'pass',
    detail: 'Client-owned movement: PositionClaim ~20Hz, server validates + rebroadcasts',
  });

  checks.push({
    id: 'sc_schema_sync', label: 'Schema sync contract',
    status: 'pass',
    detail: 'Player/NPC via MapSchema, Mount/Guild via messages',
  });

  return { id: 'server-contracts', label: 'Server Contracts', icon: '\uD83D\uDD12', checks };
}
